import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Download, Users, Clock, Calendar } from "lucide-react";
import { toast } from "@/lib/toast";

/** Admin payroll page — aggregates daily_logs.hours by employee for a date range. */
export default function AdminPayroll() {
  const today = new Date();
  const twoWeeksAgo = new Date(today); twoWeeksAgo.setDate(today.getDate() - 13);
  const [start, setStart] = useState(twoWeeksAgo.toISOString().slice(0, 10));
  const [end, setEnd] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/payroll/summary", { params: { start, end } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadXlsx = async () => {
    try {
      const r = await api.get("/payroll/export", { params: { start, end }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `payroll_${start}_to_${end}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div data-testid="admin-payroll">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="overline">Time tracking</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Payroll</h1>
          <p className="text-sm text-[#475569] mt-1">Aggregates hours from daily field logs.</p>
        </div>
        <button onClick={downloadXlsx} className="btn-accent text-sm" data-testid="payroll-export-btn">
          <Download className="w-4 h-4 mr-1.5" /> Export XLSX
        </button>
      </div>

      <div className="card-pb p-4 mb-5 flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#475569] font-medium mb-1 block">Start</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                 data-testid="payroll-start"
                 className="border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 text-sm" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#475569] font-medium mb-1 block">End</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                 data-testid="payroll-end"
                 className="border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 text-sm" />
        </div>
        <button onClick={load} disabled={loading} className="btn-outline-navy text-sm" data-testid="payroll-load">Reload</button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <Tile icon={Users} label="Crew with hours" value={data.rows.length} />
            <Tile icon={Clock} label="Total hours" value={data.total_hours.toLocaleString()} />
            <Tile icon={Calendar} label="Period" value={`${start} → ${end}`} small />
          </div>

          <div className="card-pb overflow-hidden">
            {data.rows.length === 0 ? (
              <div className="p-12 text-center text-sm text-[#475569]">No hours logged in this period.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900 text-left text-[11px] uppercase tracking-wider text-[#475569]">
                  <tr><th className="px-4 py-3">Employee</th><th className="px-4 py-3 text-right">Days worked</th><th className="px-4 py-3 text-right">Hours</th></tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.employee_id} className="border-t border-[#E2E8F0] dark:border-slate-800" data-testid={`payroll-row-${r.employee_id}`}>
                      <td className="px-4 py-3 font-medium text-[#0A2540] dark:text-white">{r.employee_name || r.employee_id}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{r.days_worked}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">{r.total_hours.toFixed(1)} h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, small }) {
  return (
    <div className="card-pb p-4">
      <div className="flex items-center gap-2 text-[#475569]"><Icon className="w-4 h-4 text-[#C9A356]" /><span className="overline">{label}</span></div>
      <div className={`font-heading font-bold text-[#0A2540] dark:text-white mt-1 tabular-nums ${small ? "text-sm" : "text-2xl"}`}>{value}</div>
    </div>
  );
}
